import * as Cesium from 'cesium';
// Forward slice helper: returns the subset of `positions` between two
// normalized fractions a <= b, with interpolated endpoints exactly at
// the fractions and the original vertices in between preserved.
const forwardSlice = (positions, segmentLengths, totalLength, a, b) => {
    const startDist = a * totalLength;
    const endDist = b * totalLength;
    const pointAtDistance = (target) => {
        let accumulated = 0;
        for (let i = 0; i < segmentLengths.length; i++) {
            const segLen = segmentLengths[i];
            if (accumulated + segLen >= target) {
                const segFraction = segLen > 0 ? (target - accumulated) / segLen : 0;
                const out = new Cesium.Cartesian3();
                Cesium.Cartesian3.lerp(positions[i], positions[i + 1], segFraction, out);
                return { point: out, segmentIdx: i };
            }
            accumulated += segLen;
        }
        return { point: Cesium.Cartesian3.clone(positions[positions.length - 1]), segmentIdx: positions.length - 2 };
    };
    const start = pointAtDistance(startDist);
    const end = pointAtDistance(endDist);
    const result = [start.point];
    for (let i = start.segmentIdx + 1; i <= end.segmentIdx; i++) {
        result.push(Cesium.Cartesian3.clone(positions[i]));
    }
    result.push(end.point);
    return result;
};
// Returns the subset of `positions` lying between two normalized
// fractions of the polyline's total length. Used by the contour-highlight
// overlay renderer to draw only the highlighted portion of a contour line.
//
// Directional: the slice travels from startFraction forward to endFraction.
//  - startFraction <= endFraction → the single arc between them.
//  - startFraction >  endFraction → wraps past the 1/0 seam, returning
//    [start → 1] joined with [0 → end]. This is what lets a closed-loop
//    contour highlight a portion that crosses its own start/end seam (the
//    dialog's "Flip side" control swaps the two fractions to pick the
//    complementary arc).
export const slicePolylineByFraction = (positions, startFraction, endFraction) => {
    if (positions.length < 2)
        return positions.slice();
    const a = Math.max(0, Math.min(1, startFraction));
    const b = Math.max(0, Math.min(1, endFraction));
    if (a <= 0 && b >= 1)
        return positions.slice();
    const segmentLengths = [];
    let totalLength = 0;
    for (let i = 1; i < positions.length; i++) {
        const len = Cesium.Cartesian3.distance(positions[i - 1], positions[i]);
        segmentLengths.push(len);
        totalLength += len;
    }
    if (totalLength === 0)
        return [Cesium.Cartesian3.clone(positions[0])];
    if (a <= b) {
        return forwardSlice(positions, segmentLengths, totalLength, a, b);
    }
    // Wrap across the seam: [a → 1] then [0 → b]. Drop the duplicated seam
    // vertex where the two pieces meet so the joined line is clean.
    const head = forwardSlice(positions, segmentLengths, totalLength, a, 1);
    const tail = forwardSlice(positions, segmentLengths, totalLength, 0, b);
    return head.concat(tail.slice(1));
};
// Inverse of interpolateAlongCartesianPolyline: projects an arbitrary world
// point onto the polyline and returns the normalized [0,1] fraction of the
// closest point along its length. Used to snap a dragged contour handle to
// the line under the cursor.
export const nearestFractionOnPolyline = (positions, point) => {
    if (positions.length < 2)
        return 0;
    const segmentLengths = [];
    let totalLength = 0;
    for (let i = 1; i < positions.length; i++) {
        const len = Cesium.Cartesian3.distance(positions[i - 1], positions[i]);
        segmentLengths.push(len);
        totalLength += len;
    }
    if (totalLength === 0)
        return 0;
    let bestDistSq = Infinity;
    let bestFraction = 0;
    let accumulated = 0;
    const ab = new Cesium.Cartesian3();
    const ap = new Cesium.Cartesian3();
    const closest = new Cesium.Cartesian3();
    for (let i = 0; i < segmentLengths.length; i++) {
        const a = positions[i];
        const b = positions[i + 1];
        const segLen = segmentLengths[i];
        Cesium.Cartesian3.subtract(b, a, ab);
        Cesium.Cartesian3.subtract(point, a, ap);
        const denom = Cesium.Cartesian3.dot(ab, ab);
        let t = denom > 0 ? Cesium.Cartesian3.dot(ap, ab) / denom : 0;
        t = Math.max(0, Math.min(1, t));
        Cesium.Cartesian3.add(a, Cesium.Cartesian3.multiplyByScalar(ab, t, closest), closest);
        const distSq = Cesium.Cartesian3.distanceSquared(point, closest);
        if (distSq < bestDistSq) {
            bestDistSq = distSq;
            bestFraction = totalLength > 0 ? (accumulated + t * segLen) / totalLength : 0;
        }
        accumulated += segLen;
    }
    return bestFraction;
};
export const interpolateAlongCartesianPolyline = (positions, fraction) => {
    if (positions.length < 2 || fraction < 0 || fraction > 1)
        return null;
    const segmentLengths = [];
    let totalLength = 0;
    for (let i = 1; i < positions.length; i++) {
        const len = Cesium.Cartesian3.distance(positions[i - 1], positions[i]);
        segmentLengths.push(len);
        totalLength += len;
    }
    if (totalLength === 0)
        return Cesium.Cartesian3.clone(positions[0]);
    const targetDistance = fraction * totalLength;
    let accumulated = 0;
    for (let i = 0; i < segmentLengths.length; i++) {
        const segLen = segmentLengths[i];
        if (accumulated + segLen >= targetDistance) {
            const segFraction = segLen > 0 ? (targetDistance - accumulated) / segLen : 0;
            const result = new Cesium.Cartesian3();
            Cesium.Cartesian3.lerp(positions[i], positions[i + 1], segFraction, result);
            return result;
        }
        accumulated += segLen;
    }
    return Cesium.Cartesian3.clone(positions[positions.length - 1]);
};
const haversineDistance = (c1, c2) => {
    const R = 6371000;
    const lat1Rad = c1.lat * Math.PI / 180;
    const lat2Rad = c2.lat * Math.PI / 180;
    const deltaLat = (c2.lat - c1.lat) * Math.PI / 180;
    const deltaLng = (c2.lng - c1.lng) * Math.PI / 180;
    const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
        Math.cos(lat1Rad) * Math.cos(lat2Rad) *
            Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
};
export const interpolateAlongCoordinatePolyline = (coords, fraction) => {
    if (coords.length < 2 || fraction < 0 || fraction > 1)
        return null;
    const segmentLengths = [];
    let totalLength = 0;
    for (let i = 1; i < coords.length; i++) {
        const len = haversineDistance(coords[i - 1], coords[i]);
        segmentLengths.push(len);
        totalLength += len;
    }
    if (totalLength === 0)
        return { ...coords[0] };
    const targetDistance = fraction * totalLength;
    let accumulated = 0;
    for (let i = 0; i < segmentLengths.length; i++) {
        const segLen = segmentLengths[i];
        if (accumulated + segLen >= targetDistance) {
            const segFraction = segLen > 0 ? (targetDistance - accumulated) / segLen : 0;
            return {
                lat: coords[i].lat + segFraction * (coords[i + 1].lat - coords[i].lat),
                lng: coords[i].lng + segFraction * (coords[i + 1].lng - coords[i].lng)
            };
        }
        accumulated += segLen;
    }
    return { ...coords[coords.length - 1] };
};
